import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { useStations } from '../hooks/useStation';

const BarMusicContext = createContext();

export const BarMusicProvider = ({ children }) => {
    const { data: stations, isLoading, error } = useStations();
    const [currentStationIndex, setCurrentStationIndex] = useState(() => {
        const savedIndex = localStorage.getItem('currentStationIndex');
        return savedIndex !== null ? parseInt(savedIndex, 10) : null;
    });
    const [isPlaying, setIsPlaying] = useState(false);
    const [offlineStations, setOfflineStations] = useState([]);
    const [audioUrl, setAudioUrl] = useState('');
    const [volume, setVolume] = useState(0.1);
    const audioRef = useRef(null);
    const [fullStation, setFullStation] = useState('');

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
        }
    }, [volume]);

    const getStationIndexByUserRegion = useCallback(async () => {
        if (!stations || stations.length === 0) return 0;
        const index = stations.findIndex(station => station.slug === 'web');
        return index !== -1 ? index : 0;
    }, [stations]);

    useEffect(() => {
        if (stations) {
            const validateStations = async () => {
                for (const station of stations) {
                    if (station.alert === 1) {
                        continue;
                    }

                    try {
                        const controller = new AbortController();
                        const timeoutId = setTimeout(() => controller.abort(), 10000);

                        const response = await fetch(station.station_link, {
                            method: 'GET',
                            signal: controller.signal
                        });

                        if (!response.ok) {
                            setOfflineStations(prev => [...new Set([...prev, station.id])]);
                        } else {
                            setOfflineStations(prev => prev.filter(id => id !== station.id));
                        }

                        controller.abort();
                        clearTimeout(timeoutId);
                    } catch (err) {
                        if (err.name !== 'AbortError') {
                            setOfflineStations(prev => [...new Set([...prev, station.id])]);
                        }
                    }
                }
            };

            validateStations();
        }
    }, [stations]);

    useEffect(() => {
        if (stations && stations.length > 0) {
            const initializeStation = async () => {
                let index = currentStationIndex;
                if (index === null) {
                    index = await getStationIndexByUserRegion();
                    setCurrentStationIndex(index);
                    localStorage.setItem('currentStationIndex', index);
                }
                setAudioUrl(stations[index]?.station_link);
                const station = stations[index];
                const stationName = `Altavoz ${station?.name}`;
                const fullStationName = station?.station === 0 ? stationName : `${stationName} ${station?.station} FM`;
                setFullStation(fullStationName);
            };
            initializeStation();
        }
    }, [stations, getStationIndexByUserRegion, currentStationIndex]);

    const playAudio = useCallback(() => {
        if (audioRef.current) {
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
                playPromise.then(() => {
                    setIsPlaying(true);
                }).catch(error => {
                    // Handle play error
                });
            }
        }
    }, []);

    useEffect(() => {
        if (audioRef.current && audioUrl) {
            audioRef.current.src = audioUrl;
            audioRef.current.load();
            audioRef.current.addEventListener('canplaythrough', playAudio, { once: true });
        }
    }, [audioUrl, playAudio]);

    const changeStation = (direction) => {
        if (!stations || stations.length === 0) return;
        const newIndex = direction === 'next'
            ? (currentStationIndex + 1) % stations.length
            : (currentStationIndex - 1 + stations.length) % stations.length;
        setCurrentStationIndex(newIndex);
        localStorage.setItem('currentStationIndex', newIndex);
        setAudioUrl(stations[newIndex].station_link);
        const station = stations[newIndex];
        const stationName = `Altavoz ${station.name}`;
        const fullStationName = station.station === 0 ? stationName : `${stationName} ${station.station} FM`;
        setFullStation(fullStationName);
    };

    const togglePlayPause = () => {
        setIsPlaying(prevState => !prevState);
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                playAudio();
            }
        }
    };

    const handleLoadedMetadata = () => {
        playAudio();
    };

    const handleVolumeChange = (e) => {
        setVolume(e.target.value);
    };

    const handleStationClick = (index) => {
        if (currentStationIndex === index) {
            togglePlayPause();
        } else {
            setCurrentStationIndex(index);
            localStorage.setItem('currentStationIndex', index);
            setAudioUrl(stations[index].station_link);
            const station = stations[index];
            const stationName = `Altavoz ${station.name}`;
            const fullStationName = station.station === 0 ? stationName : `${stationName} ${station.station} FM`;
            setFullStation(fullStationName);
        }
    };

    if (isLoading) return null;

    if (error) return null;

    if (!stations) return null;

    const value = {
        stations,
        currentStationIndex,
        isLoading,
        isPlaying,
        audioUrl,
        volume,
        audioRef,
        fullStation,
        getStationIndexByUserRegion,
        changeStation,
        togglePlayPause,
        handleLoadedMetadata,
        handleVolumeChange,
        handleStationClick,
        offlineStations
    };

    return <BarMusicContext.Provider value={value}>{children}</BarMusicContext.Provider>;
};

export default BarMusicContext;
